// Shared constants (client + server). No secrets, no server-only data.

export const SUBJECTS = [
  { id: "dansk", name: "Dansk", color: "#ff6b6b", icon: "book" },
  { id: "tysk", name: "Tysk", color: "#ffd166", icon: "language" },
  { id: "engelsk", name: "Engelsk", color: "#4cc9f0", icon: "globe" },
  { id: "fysikkemi", name: "Fysik/Kemi", color: "#b388ff", icon: "flask" },
  { id: "matematik", name: "Matematik", color: "#7CFC8A", icon: "calc" },
  { id: "historie", name: "Historie", color: "#ff9f43", icon: "scroll" },
  { id: "biologi", name: "Biologi", color: "#2ee6a6", icon: "leaf" },
  { id: "religion", name: "Religion", color: "#f78fb3", icon: "star" },
] as const;

export type Subject = (typeof SUBJECTS)[number]["id"];
export const SUBJECT_IDS = SUBJECTS.map((s) => s.id) as Subject[];
export const subjectName = (id: string) => SUBJECTS.find((s) => s.id === id)?.name ?? id;
export const isSubject = (v: unknown): v is Subject => typeof v === "string" && (SUBJECT_IDS as string[]).includes(v);

export const GRADES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
export type Grade = number;
export const gradeLabel = (g: number) => `${g}. klasse`;
export type GradeBand = "indskoling" | "mellemtrin" | "udskoling";
export const gradeBand = (g: number): GradeBand => (g <= 3 ? "indskoling" : g <= 6 ? "mellemtrin" : "udskoling");
export const GRADE_BAND_LABEL: Record<GradeBand, string> = {
  indskoling: "Indskoling (0.–3.)",
  mellemtrin: "Mellemtrin (4.–6.)",
  udskoling: "Udskoling (7.–9.)",
};

export type Difficulty = 1 | 2 | 3;
export const DIFFICULTY_LABEL: Record<Difficulty, string> = { 1: "Let", 2: "Mellem", 3: "Svær" };
/** "mix" = grade-appropriate blend. */
export type DifficultyChoice = Difficulty | "mix";

export const AGE_BANDS = ["5-7", "8-10", "11-13", "14-16", "adult"] as const;
export type AgeBand = (typeof AGE_BANDS)[number];
/** Converts an exact age to a coarse band. The exact age is NEVER stored (data minimisation). */
export function ageToBand(age: number): AgeBand {
  if (age <= 7) return "5-7";
  if (age <= 10) return "8-10";
  if (age <= 13) return "11-13";
  if (age <= 16) return "14-16";
  return "adult";
}

export const GAME_IDS = [
  "monopoly",
  "kortkamp",
  "slagskibe",
  "speedrun",
  "fire",
  "skak",
  "kryds",
  "quizduel",
  "taarn",
  "videnraes",
  "skattejagt",
  "ludo",
  "yatzy",
  "sudoku",
  "videnspar",
  "verden",
] as const;
export type GameId = (typeof GAME_IDS)[number];
export const isGameId = (v: unknown): v is GameId => typeof v === "string" && (GAME_IDS as readonly string[]).includes(v);

export type GameKind = "turn" | "sim" | "solo";
export interface GameMeta {
  id: GameId;
  name: string;
  tagline: string;
  kind: GameKind;
  /** human-vs-human online (lobby code / quick match) */
  online: boolean;
  /** playable vs a computer opponent */
  bot: boolean;
  /** playable alone without opponent */
  solo: boolean;
  /** can be run by a teacher for a whole class */
  classSession: boolean;
  minPlayers: number;
  maxPlayers: number;
  tags: string[];
  rules: string[];
  accent: string;
}

export const GAMES: GameMeta[] = [
  {
    id: "monopoly",
    name: "EduPoly",
    tagline: "Køb, byg og bliv rigest – det klassiske ejendomsspil, hvor viden giver rabat.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 4,
    tags: ["Brætspil", "Strategi", "2–4 spillere"],
    accent: "#16a34a",
    rules: [
      "2–4 spillere starter med 1500 og står på START. Slå med to terninger og ryk rundt på de 40 felter. Går du forbi START, får du 200.",
      "Lander du på en ejendom ingen ejer, får du et spørgsmål: Svar rigtigt, og du må købe den. Svar forkert, og den sendes på auktion, hvor alle kan byde.",
      "Lander du på en andens ejendom, betaler du leje. Svarer du rigtigt på et spørgsmål, betaler du kun halv leje (det samme gælder skat).",
      "Ejer du alle ejendomme i en farve, fordobles lejen, og du må bygge huse (op til 4) og derefter et hotel. Du skal bygge jævnt, og hver bygning kræver et rigtigt svar. Svarer du forkert, bygges der ikke mere i den tur.",
      "To ens på terningerne giver et ekstra slag. Tre ens i træk sender dig i fængsel. I fængsel kan du betale 50, bruge et løsladelseskort, slå to ens (op til tre forsøg) eller svare rigtigt på et spørgsmål for at komme gratis ud.",
      "Chancen og Fælleskassen giver kort med glæder og udgifter. Ejendomme kan pantsættes (får halv pris) og indløses (pris + 10 %).",
      "Du kan bytte ejendomme og penge med andre spillere. Kan du ikke betale en gæld, skal du sælge bygninger, pantsætte – eller gå konkurs.",
      "Sidste spiller i spillet vinder. For at spillet ikke varer for længe, slutter det efter et fast antal runder, og den med flest værdier vinder.",
    ],
  },
  {
    id: "kortkamp",
    name: "Kortkamp",
    tagline: "Svar rigtigt, læg dit kort, og bliv først af med hele hånden.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 4,
    tags: ["Kort", "Taktik"],
    accent: "#ff6b6b",
    rules: [
      "2–4 spillere. Alle starter med 7 kort. Et kort har en farve (rød, gul, grøn, blå) og et tal 0–9, eller det er et specialkort. Spillet har 108 kort ligesom det klassiske farve-og-tal-kortspil.",
      "Du skal lægge et kort med samme farve eller samme tal/symbol som det øverste kort. Joker kan altid lægges, og du vælger den nye farve.",
      "Kan du ikke lægge noget, trækker du ét kort. Passer det, må du lægge det – ellers beholder du det, og turen går videre.",
      "Specialkort: ⏭ Spring over, ⇄ Skift retning (med to spillere virker det som spring over), +2 (næste spiller skal trække to) og Joker +4 (næste skal trække fire – må kun lægges, hvis du ikke har en anden i den aktive farve).",
      "EduBattle-reglen: Rammes du af +2 eller +4, får du et spørgsmål. Svarer du rigtigt, slipper du helt og spiller din tur som normalt. Svarer du forkert, trækker du kortene og springes over.",
      "Når du lægger dit næstsidste kort, skal du sige EDU! (tryk på EDU-knappen). Glemmer du det, trækker du to kort.",
      "Er trækbunken tom, blandes de gamle kort. Den første, der er af med alle kort, vinder. Til øvelse (uden straf) kan du spille uden spørgsmål.",
    ],
  },
  {
    id: "slagskibe",
    name: "Sænke slagskibe",
    tagline: "Skjul flåden, find modstanderens skibe – ét rigtigt svar giver ét skud.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 2,
    tags: ["Strategi", "Grid"],
    accent: "#4cc9f0",
    rules: [
      "Hver spiller placerer en flåde på et 10×10-felt: 1 hangarskib (5 felter), 1 slagskib (4), 2 krydsere/ubåd (3 og 3) og 1 destroyer (2). Skibe ligger vandret eller lodret og må ikke ligge oven i hinanden.",
      "Skiftevis får du et spørgsmål. Et rigtigt svar giver ét skud på et felt hos modstanderen (fx C7). Et forkert svar koster skuddet.",
      "Du får at vide, om du ramte eller missede, og når et skib er sænket. Hver tur består af ét skud – både ved hit og miss går turen videre.",
      "Den, der først sænker alle modstanderens skibe, vinder.",
    ],
  },
  {
    id: "speedrun",
    name: "Speedrun",
    tagline: "Så mange rigtige svar som muligt, før tiden løber ud.",
    kind: "sim",
    online: false,
    bot: false,
    solo: true,
    classSession: true,
    minPlayers: 1,
    maxPlayers: 1,
    tags: ["Tid", "Solo"],
    accent: "#ff9f43",
    rules: [
      "Du har en fast tid (serverens ur, ikke dit). Svar på så mange spørgsmål som muligt.",
      "Rigtigt svar: 100 point + fartbonus (op til 50) + stribebonus (op til 50).",
      "Forkert svar: −25 point og striben nulstilles. Ren gætteri kan derfor ikke betale sig.",
      "Efter spillet gennemgår du alle spørgsmål med forklaringer.",
    ],
  },
  {
    id: "fire",
    name: "Fire på stribe",
    tagline: "Svar rigtigt for at droppe din brik – få fire på række.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 2,
    tags: ["Brætspil", "Taktik"],
    accent: "#ffd166",
    rules: [
      "Brættet er 7 kolonner × 6 rækker. Brikker falder ned i den laveste ledige plads.",
      "Skiftevis får du et spørgsmål. Svarer du rigtigt, må du placere en brik i en kolonne.",
      "Svarer du forkert, springer du turen over (i øvemode sker der ingenting).",
      "Første spiller med fire på række – vandret, lodret eller diagonalt – vinder. Fuldt bræt giver uafgjort.",
    ],
  },
  {
    id: "skak",
    name: "Skak",
    tagline: "Ægte skak: ét rigtigt svar giver dig lov til at flytte.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 2,
    tags: ["Klassiker", "Strategi"],
    accent: "#b388ff",
    rules: [
      "Almindelige skakregler: alle brikkers træk, skak, skakmat, pat, rokade, en passant og bondeforvandling.",
      "Før hvert træk får du et fagligt spørgsmål. Rigtigt svar = du må flytte. Forkert svar = du springer turen over (i øvemode sker der intet).",
      "Du kan ikke tage kongen: spillet slutter ved skakmat. Femti-trækssregel og trefold gentagelse giver uafgjort.",
      "Tutorial: åbn 'Sådan flytter brikkerne' i spillet for en kort guide til børn.",
    ],
  },
  {
    id: "kryds",
    name: "Kryds og bolle",
    tagline: "Det klassiske 3×3-spil – kun med et rigtigt svar får du lov at sætte dit mærke.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 2,
    tags: ["Klassiker", "Hurtig"],
    accent: "#7CFC8A",
    rules: [
      "Skiftevis får du et spørgsmål. Rigtigt svar = du vælger et frit felt.",
      "Forkert svar = du mister turen (i øvemode sker der intet).",
      "Tre på række vandret, lodret eller diagonalt vinder. Fuldt bræt uden vinder er uafgjort.",
    ],
  },
  {
    id: "quizduel",
    name: "Quizduel",
    tagline: "Samme spørgsmål, samme tid – hvem svarer rigtigt og hurtigst?",
    kind: "sim",
    online: true,
    bot: true,
    solo: false,
    classSession: true,
    minPlayers: 2,
    maxPlayers: 8,
    tags: ["Quiz", "Duel"],
    accent: "#f78fb3",
    rules: [
      "Begge spillere får præcis de samme spørgsmål på samme tid.",
      "Rigtigt svar: 100 point + fartbonus (op til 50, samme formel for alle, målt af serveren).",
      "Forkert svar eller timeout: 0 point. Efter hver runde ser I det rigtige svar og forklaringen.",
      "Flest point efter alle runder vinder. Samme point giver uafgjort.",
    ],
  },
  {
    id: "taarn",
    name: "Tårnforsvar",
    tagline: "Rigtige svar giver energi til tårne – stands bølgerne, før de rammer basen.",
    kind: "solo",
    online: false,
    bot: false,
    solo: true,
    classSession: false,
    minPlayers: 1,
    maxPlayers: 1,
    tags: ["Strategi", "Solo"],
    accent: "#2ee6a6",
    rules: [
      "Vælg en af fem baner (Skovstien, Ørkenoasen, Vinterlandet, Vulkanøen eller Slikland) og en sværhedsgrad: Let, Mellem eller Svær. Sværhedsgraden ændrer både spørgsmålene og banen (antal, fart og styrke på balloner, liv og start-guld).",
      "Balloner går ad en fast vej mod din base. Der er 15 runder.",
      "Svar på spørgsmål for at optjene guld: rigtigt svar giver guld efter spørgsmålets sværhedsgrad, forkert giver ingenting.",
      "Brug guld på at bygge tårne eller opgradere dem på felter ved vejen. Nye tårne låses op, når du stiger i niveau.",
      "Start runden, når du er klar. Serveren simulerer kampen, og du ser den afspillet.",
      "Klarer du runde 15, har du vundet – og du kan trykke på «Spil videre» for at fortsætte i runder, der bliver sværere og sværere. Er basen tom for liv, er spillet slut, og dine runder tæller med.",
    ],
  },
  {
    id: "videnraes",
    name: "Vidensræs",
    tagline: "Ræs mod målstregen – hvert rigtigt svar giver fart.",
    kind: "sim",
    online: true,
    bot: true,
    solo: false,
    classSession: true,
    minPlayers: 2,
    maxPlayers: 8,
    tags: ["Race", "Tempo"],
    accent: "#ff6b6b",
    rules: [
      "Banen er 100 meter lang. Alle får de samme spørgsmål i samme rækkefølge, men i eget tempo.",
      "Rigtigt svar: du kører 8–16 meter frem (sværere spørgsmål giver mere). Stribe på 3+ giver op til 6 meter i boost.",
      "Forkert svar: du står stille og skal vente 2 sekunder, før du får næste spørgsmål (ingen straf ud over det).",
      "Første over målstregen vinder. Bilen i animationen viser den rigtige position fra serveren.",
    ],
  },
  {
    id: "skattejagt",
    name: "Skattejagt",
    tagline: "Udforsk ruinen, lås porte op med viden og find skatten.",
    kind: "solo",
    online: false,
    bot: false,
    solo: true,
    classSession: false,
    minPlayers: 1,
    maxPlayers: 1,
    tags: ["Eventyr", "Solo"],
    accent: "#ffd166",
    rules: [
      "Du står i en ruin med kamre, låste porte og en skattekiste.",
      "Gå til et nabofelt. Er der en låst port, skal du svare rigtigt for at åbne den. Portene forbliver åbne bagefter.",
      "Svar forkert, og du mister 1 af dine 5 fakler – men du kan prøve igen med et nyt spørgsmål.",
      "Nogle kamre indeholder nøgler og spor. Den sidste port kræver 2 nøgler.",
      "Find skatten for at vinde. Løber du tør for fakler, er jagten slut.",
    ],
  },
  {
    id: "ludo",
    name: "Ludo",
    tagline: "Slå en sekser, ræs rundt om brættet og kom først hjem – men du skal svare rigtigt for at få lov at slå.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 4,
    tags: ["Brætspil", "Terning", "2–4 spillere"],
    accent: "#f43f5e",
    rules: [
      "2–4 spillere. Alle har 2 brikker, der starter i deres hjørne (basen). Målet er at få begge brikker rundt om brættet og helt ind i målet i midten.",
      "EduBattle-reglen: Før hver tur får du et spørgsmål. Svarer du rigtigt, slår brættet terningen for dig. Svarer du forkert, går turen videre til næste spiller.",
      "En sekser henter en brik ud af basen og ind på dit startfelt. En sekser giver også et ekstra slag (med et nyt spørgsmål). Har du ingen brikker ude, får du op til tre slag, så du ikke venter for evigt.",
      "Slår du et tal, skal du vælge hvilken brik der rykker. Er der kun ét træk, rykker brikken af sig selv.",
      "Lander du på et felt, hvor en anden spillers brik står, sendes den tilbage til sin base. Det gælder dog ikke på startfelterne og de felter med en stjerne – dér er alle trygge.",
      "Efter en hel runde drejer din brik ind på sin egen farvede vej mod målet. Du skal ramme målet med præcist det rigtige antal øjne.",
      "Den første spiller, der får begge brikker i mål, vinder. I øvemode (uden straf) får du altid lov at slå.",
    ],
  },
  {
    id: "yatzy",
    name: "Yatzy",
    tagline: "Fem terninger, tre slag og femten felter – svar rigtigt for at få alle dine slag.",
    kind: "turn",
    online: true,
    bot: true,
    solo: false,
    classSession: false,
    minPlayers: 2,
    maxPlayers: 4,
    tags: ["Terning", "Point", "2–4 spillere"],
    accent: "#f59e0b",
    rules: [
      "2–4 spillere spiller 15 runder. I hver runde skal du udfylde ét felt på dit pointark – og alle felter bruges præcis én gang.",
      "EduBattle-reglen: Hver tur starter med et spørgsmål. Svarer du rigtigt, slår du alle fem terninger og får op til to slag mere, hvor du selv vælger hvilke terninger du beholder. Svarer du forkert, får du kun ét slag – du skal stadig udfylde et felt.",
      "Felterne: Enere til Seksere (summen af den slags terninger), Ét par, To par, Tre ens, Fire ens, Lille straight (1-2-3-4-5 = 15), Stor straight (2-3-4-5-6 = 20), Fuldt hus (tre ens + to ens), Chance (summen af alle) og Yatzy (fem ens = 50).",
      "Bonus: Får du mindst 63 point i de seks øverste felter (Enere–Seksere), får du 50 ekstra point.",
      "Passer terningerne ikke til et felt, må du skrive 0 i det – men vælg klogt, for det felt kan ikke bruges igen.",
      "Efter 15 runder vinder den med flest point. Du kan øve uden straf: så får du altid alle tre slag.",
    ],
  },
  {
    id: "sudoku",
    name: "Sudoku",
    tagline: "Fyld tallene i, så de aldrig gentages – og åbn hver boks med viden.",
    kind: "solo",
    online: false,
    bot: false,
    solo: true,
    classSession: false,
    minPlayers: 1,
    maxPlayers: 1,
    tags: ["Logik", "Tal", "Solo"],
    accent: "#38bdf8",
    rules: [
      "I 0.–2. klasse er brættet 4×4 (tallene 1–4), i 3.–5. klasse 6×6 (tallene 1–6), og fra 6. klasse er det det klassiske 9×9. Der er kun én rigtig løsning.",
      "Hvert tal må kun stå én gang i hver række, hver kolonne og hver boks (de tykt indrammede områder).",
      "EduBattle-reglen: Boksene er låst i tåge. Tryk på en låst boks og svar rigtigt på et spørgsmål for at åbne den. Du kan se tallene i de låste bokse og tænke i forvejen, men du kan først skrive i dem, når de er åbnet.",
      "Svarer du forkert, prøver du bare med et nyt spørgsmål – der er ingen straf.",
      "Tryk på et felt og vælg et tal. Er tallet forkert, bliver det ikke sat ind, og du får en venlig besked (det koster kun nogle få bonuspoint).",
      "Sidder du fast? Tryk på «Hint» og svar på et spørgsmål – så afslører spillet det næste lette felt for dig.",
      "Du har vundet, når alle felter er udfyldt.",
    ],
  },
  {
    id: "videnspar",
    name: "Videnspar",
    tagline: "Find to ens kort – og behold dem ved at svare rigtigt.",
    kind: "solo",
    online: false,
    bot: false,
    solo: true,
    classSession: false,
    minPlayers: 1,
    maxPlayers: 1,
    tags: ["Hukommelse", "Kort", "Solo"],
    accent: "#34d399",
    rules: [
      "Alle kort ligger med bagsiden op. Vend to kort ad gangen og find de par, der er ens. I 0.–2. klasse er der 6 par, i 3.–5. klasse 8 par, og fra 6. klasse 10 par.",
      "Vender du to forskellige kort, vender de om igen. Husk, hvor de lå!",
      "Finder du to ens kort, får du et spørgsmål. Svarer du rigtigt, er parret dit. Svarer du forkert, vender kortene om igen – men nu ved du, hvor de ligger, så du kan prøve igen med et nyt spørgsmål.",
      "Finder du flere par i træk uden at fejle, får du en stribe og flere point.",
      "Du har vundet, når alle par er fundet.",
    ],
  },
  {
    id: "verden",
    name: "Fri Verden",
    tagline: "Kør rundt på en 3D-ø med din egen figur – løs missioner og kør ræs, hvor du svarer for at komme videre.",
    kind: "solo",
    online: false,
    bot: false,
    solo: true,
    classSession: false,
    minPlayers: 1,
    maxPlayers: 1,
    tags: ["3D", "Åben verden", "Venner"],
    accent: "#22d3ee",
    rules: [
      "Du styrer din egen figur rundt på en stor 3D-ø. Gå, løb – eller sæt dig ind i en bil og kør. Din figur er den, du har valgt i Shoppen.",
      "Missioner: Gå hen til en mission (de lysende søjler på kortet) og tryk «Start». Hvert stop på ruten giver ét spørgsmål – svar rigtigt for at komme videre.",
      "Ræs: Kør 3–4 omgange mod computerkørere. Efter hver omgang holder du ved quiz-linjen og besvarer 3 spørgsmål, før du må køre videre. Tiden står stille, mens du tænker – du taber aldrig et ræs på at tænke dig om.",
      "Svarer du forkert, får du bare et nyt spørgsmål. Efter to forkerte i træk viser spillet svaret, og du må køre videre.",
      "Mønter og XP får du for dine rigtige svar – ligesom i alle de andre spil. Du kan kun få mønter for det, du svarer på, så det betaler sig ikke at køre hurtigere, men det er sjovere!",
      "Venner: Har du venner på siden, kan du besøge dem i deres verden (eller bede dem komme til din) fra Venner-siden. Man kan ikke skrive til hinanden, men man kan vinke og juble.",
      "Når du er færdig, trykker du på «Afslut turen» og får dine mønter.",
    ],
  },
];

export const gameMeta = (id: string): GameMeta | undefined => GAMES.find((g) => g.id === id);

export type Role = "student" | "teacher" | "admin";

export const COSMETIC_CATEGORIES = [
  { id: "hero", name: "Helte" },
  { id: "skin", name: "Figurer" },
  { id: "outfit", name: "Outfits" },
  { id: "hat", name: "Hatte & hjelme" },
  { id: "accessory", name: "Briller & masker" },
  { id: "back", name: "Rygge & vinger" },
  { id: "hand", name: "Udstyr i hånden" },
  { id: "pet", name: "Kæledyr" },
  { id: "aura", name: "Auraer & effekter" },
  { id: "background", name: "Baggrunde" },
  { id: "frame", name: "Profilrammer" },
  { id: "banner", name: "Bannere" },
  { id: "title", name: "Titler" },
  { id: "emote", name: "Emotes & sejrsanimationer" },
  { id: "cardback", name: "Kortbagsider" },
  { id: "boardtheme", name: "Spilbrætstemaer" },
  /* Fri Verden: the garage and the houses (src/shared/fleet.ts) */
  { id: "car", name: "Biler" },
  { id: "boat", name: "Både" },
  { id: "plane", name: "Fly" },
  { id: "heli", name: "Helikoptere" },
  { id: "property", name: "Huse" },
] as const;
/** the categories of things that live in Fri Verden – they are never part of a figure and never shown on another player's avatar */
export const WORLD_CATEGORIES = ["car", "boat", "plane", "heli", "property"] as const;
export type CosmeticCategory = (typeof COSMETIC_CATEGORIES)[number]["id"];

export const RARITY_LABEL = { common: "Almindelig", rare: "Sjælden", epic: "Episk", legendary: "Legendarisk" } as const;
export type Rarity = keyof typeof RARITY_LABEL;

export const BOT_LEVELS = ["easy", "medium", "hard"] as const;
export type BotLevel = (typeof BOT_LEVELS)[number];
export const BOT_LEVEL_LABEL: Record<BotLevel, string> = { easy: "Begynder-bot", medium: "Mellem-bot", hard: "Mester-bot" };

/** Standard timer lengths. */
export const QUESTION_TIMER_DEFAULT = 30;
